'use client';

import { c as _c } from "react/compiler-runtime";
import { forwardRef, type ReactNode, useState } from 'react';
import type { IconName } from '../Icon';
import { ButtonIcon, buttonVariant, getIconClass, getIconSize, Grouped, Size, Variant } from './shared';
import { cn } from '~/utils';
import type { ClassValue } from 'clsx';
type ButtonProps = {
  /** @default 'button' */
  as?: React.ElementType;
  /** @default 'button' */
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  /** @default 'secondary' */
  variant?: (typeof Variant)[keyof typeof Variant];
  /** @default 'md' */
  size?: (typeof Size)[keyof typeof Size];
  grouped?: (typeof Grouped)[keyof typeof Grouped];
  iconAfter?: IconName;
  iconBefore?: IconName;
  iconClassName?: string;
  className?: ClassValue;
  children?: ReactNode;
  isLoading?: boolean;
  disabled?: boolean;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void | Promise<unknown>;
  /** Form ID */
  form?: React.ButtonHTMLAttributes<HTMLButtonElement>['form'];
};
function ButtonComponent(t0, ref) {
  const $ = _c(79);
  let children;
  let className;
  let grouped;
  let href;
  let iconAfter;
  let iconBefore;
  let iconClassName;
  let isExternalLoading;
  let onClick;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      as: t1,
      type: t2,
      variant: t3,
      size: t4,
      grouped,
      iconAfter,
      iconBefore,
      iconClassName,
      className,
      children,
      isLoading: isExternalLoading,
      disabled: t5,
      href,
      onClick,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = grouped;
    $[4] = href;
    $[5] = iconAfter;
    $[6] = iconBefore;
    $[7] = iconClassName;
    $[8] = isExternalLoading;
    $[9] = onClick;
    $[10] = rest;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    children = $[1];
    className = $[2];
    grouped = $[3];
    href = $[4];
    iconAfter = $[5];
    iconBefore = $[6];
    iconClassName = $[7];
    isExternalLoading = $[8];
    onClick = $[9];
    rest = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
  }
  const as = t1 === undefined ? "button" : t1;
  const type = t2 === undefined ? "button" : t2;
  const variant = t3 === undefined ? "secondary" : t3;
  const size = t4 === undefined ? "md" : t4;
  const disabled = t5 === undefined ? false : t5;
  const [isLoading, setIsLoading] = useState(false);
  const Component = as;
  let t6;
  if ($[16] !== size) {
    t6 = getIconSize(size);
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const iconSize = t6;
  let T0;
  let hasIconAfter;
  let hasIconBefore;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t7;
  let t8;
  let t9;
  if ($[18] !== Component || $[19] !== as || $[20] !== className || $[21] !== disabled || $[22] !== grouped || $[23] !== href || $[24] !== iconAfter || $[25] !== iconBefore || $[26] !== iconClassName || $[27] !== iconSize || $[28] !== isExternalLoading || $[29] !== isLoading || $[30] !== onClick || $[31] !== ref || $[32] !== rest || $[33] !== size || $[34] !== type || $[35] !== variant) {
    const iconClass = getIconClass(variant);
    hasIconAfter = !!iconAfter;
    hasIconBefore = !!iconBefore;
    T0 = Component;
    t7 = ref;
    t8 = as === "button" ? type : "button";
    t9 = as === "a" ? href : undefined;
    if ($[47] !== className || $[48] !== grouped || $[49] !== size || $[50] !== variant) {
      t10 = cn(buttonVariant({
        size,
        variant,
        grouped
      }), className);
      $[47] = className;
      $[48] = grouped;
      $[49] = size;
      $[50] = variant;
      $[51] = t10;
    } else {
      t10 = $[51];
    }
    if ($[52] !== isLoading || $[53] !== onClick) {
      t11 = onClick === undefined ? undefined : async event => {
        event.stopPropagation();
        if (isLoading) {
          return;
        }
        const returnValue = onClick?.(event);
        if (returnValue instanceof Promise) {
          setIsLoading(true);
          await returnValue.finally(() => setIsLoading(false));
        }
      };
      $[52] = isLoading;
      $[53] = onClick;
      $[54] = t11;
    } else {
      t11 = $[54];
    }
    t12 = rest;
    t13 = disabled || isLoading || isExternalLoading;
    t14 = (isLoading || isExternalLoading) && <ButtonIcon name="spinner" size={iconSize} className={cn("animate-spin", iconClass, iconClassName ?? "")} />;
    $[18] = Component;
    $[19] = as;
    $[20] = className;
    $[21] = disabled;
    $[22] = grouped;
    $[23] = href;
    $[24] = iconAfter;
    $[25] = iconBefore;
    $[26] = iconClassName;
    $[27] = iconSize;
    $[28] = isExternalLoading;
    $[29] = isLoading;
    $[30] = onClick;
    $[31] = ref;
    $[32] = rest;
    $[33] = size;
    $[34] = type;
    $[35] = variant;
    $[36] = T0;
    $[37] = hasIconAfter;
    $[38] = hasIconBefore;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
  } else {
    T0 = $[36];
    hasIconAfter = $[37];
    hasIconBefore = $[38];
    t10 = $[39];
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
    t7 = $[44];
    t8 = $[45];
    t9 = $[46];
  }
  let t15;
  if ($[55] !== hasIconBefore || $[56] !== iconBefore || $[57] !== iconClassName || $[58] !== iconSize || $[59] !== isLoading) {
    t15 = !isLoading && hasIconBefore ? <ButtonIcon size={iconSize} name={iconBefore} className={iconClassName ?? ""} /> : null;
    $[55] = hasIconBefore;
    $[56] = iconBefore;
    $[57] = iconClassName;
    $[58] = iconSize;
    $[59] = isLoading;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== hasIconAfter || $[62] !== iconAfter || $[63] !== iconClassName || $[64] !== iconSize) {
    t16 = hasIconAfter ? <ButtonIcon size={iconSize} name={iconAfter} className={iconClassName ?? ""} /> : null;
    $[61] = hasIconAfter;
    $[62] = iconAfter;
    $[63] = iconClassName;
    $[64] = iconSize;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== T0 || $[67] !== children || $[68] !== t10 || $[69] !== t11 || $[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t16 || $[75] !== t7 || $[76] !== t8 || $[77] !== t9) {
    t17 = <T0 ref={t7} type={t8} href={t9} className={t10} onClick={t11} {...t12} disabled={t13}>{t14}{t15}{children}{t16}</T0>;
    $[66] = T0;
    $[67] = children;
    $[68] = t10;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t16;
    $[75] = t7;
    $[76] = t8;
    $[77] = t9;
    $[78] = t17;
  } else {
    t17 = $[78];
  }
  return t17;
}
interface ButtonType extends React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLElement>> {
  Variant: typeof Variant;
}
const Button = forwardRef(ButtonComponent) as ButtonType;
Button.Variant = Variant;
declare namespace Button {
  export type Props = ButtonProps;
}
export default Button;